/**
 * Persisted Studio render state, as returned by ST-4789's
 * `StudioRenderStateDto` (`studioRender` on a type's detail DTO).
 */
export type StudioRenderStatus = 'none' | 'processing' | 'error';

export type StudioRenderFailureCode =
  | 'render-error'
  | 'submit-failed'
  | 'render-timeout'
  | 'store-failed';

export interface StudioRenderState {
  status: StudioRenderStatus;
  /** One of the four codes, or null for legacy and post-render errors. */
  failureCode?: StudioRenderFailureCode | string | null;
  canRetry: boolean;
}

export type StudioRenderPhase = 'idle' | 'processing' | 'failed' | 'timedOut';

export interface StudioRenderView {
  phase: StudioRenderPhase;
  isProcessing: boolean;
  /** True for failed and timed-out renders. */
  isFailed: boolean;
  failureCode: string | null;
  canRetry: boolean;
}

export const STUDIO_RENDER_GENERIC_FAILURE_MESSAGE =
  'There was an error processing this asset';

const IDLE_VIEW: StudioRenderView = {
  phase: 'idle',
  isProcessing: false,
  isFailed: false,
  failureCode: null,
  canRetry: false,
};

const PROCESSING_VIEW: StudioRenderView = {
  ...IDLE_VIEW,
  phase: 'processing',
  isProcessing: true,
};

/** Maps persisted render state to what a surface shows. Missing → idle. */
export const toStudioRenderView = (
  state?: StudioRenderState | null
): StudioRenderView => {
  if (state?.status === 'processing') {
    return PROCESSING_VIEW;
  }

  if (state?.status === 'error') {
    const failureCode = state.failureCode || null;
    return {
      phase: failureCode === 'render-timeout' ? 'timedOut' : 'failed',
      isProcessing: false,
      isFailed: true,
      failureCode,
      canRetry: !!state.canRetry,
    };
  }

  return IDLE_VIEW;
};

/** Copy chosen per failure code; never the raw generator error. */
export const studioRenderFailureMessage = (code?: string | null): string => {
  switch (code) {
    case 'render-error':
      return "Studio couldn't render this asset.";
    case 'render-timeout':
      return 'The Studio render timed out.';
    case 'submit-failed':
      return "Studio couldn't start this render.";
    case 'store-failed':
      return "Studio rendered this asset but it couldn't be saved.";
    default:
      return STUDIO_RENDER_GENERIC_FAILURE_MESSAGE;
  }
};
